// StylesFilter.js - Modified to work only in accordion mode

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateFilters } from "store/filters/operations";
import { selectFilters } from "store/filters/selectors";
import { Checkbox } from "reusableComponents";
import { FiltersButton } from "..";
import { getPlaceStyles } from "services/homePage.services";
import { useTranslation } from "react-i18next";
import { useLanguage } from "context/languageContext";
import styles from "./styles.module.scss";

const StylesFilter = ({
  // Props for external control (like in mobile form)
  onStyleChange,
  selectedStyle,
}) => {
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  
  const [showStylesDropdown, setShowStylesDropdown] = useState(true);
  const [stylesOptions, setStylesOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { language } = useLanguage();

  // Use prop if provided, otherwise use Redux state
  const currentStyle = selectedStyle !== undefined ? selectedStyle : filters.style;

  useEffect(() => {
    const fetchStyles = async () => {
      setLoading(true);
      setError(null);
      try {
        const stylesData = await getPlaceStyles(language);
        setStylesOptions([
          { id: null, name: t("search.allStyles") || "All Styles" },
          ...stylesData.map(style => ({
            id: style.id,
            name: style.name
          }))
        ]);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch styles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStyles();
  }, [language, t]);

  const toggleShowStylesDropdown = () => {
    setShowStylesDropdown((prevState) => !prevState);
  };

  const handleStyleChange = (option) => {
    if (onStyleChange) {
      // If callback provided, use it (for mobile form)
      onStyleChange(option.id);
    } else {
      // Otherwise use Redux dispatch
      dispatch(updateFilters({ style: option.id }));
    }
    
    // Close the accordion after selection
    setShowStylesDropdown(false);
  };

  // Get the name of the selected style for the button
  const getSelectedStyleName = () => {
    if (!currentStyle) {
      return t("search.allStyles") || "All Styles";
    }
    const selectedStyleObj = stylesOptions.find(style => style.id === currentStyle);
    return selectedStyleObj ? selectedStyleObj.name : (t("search.allStyles") || "All Styles");
  };

  // Get display text for the button
  const getDisplayText = () => {
    if (loading) return t("search.loading") || "Loading...";
    if (error) return t("search.defaultStyle") || "Style";
    return getSelectedStyleName();
  };

  // Check if a style option is selected
  const isStyleSelected = (style) => {
    if (style.id === null) {
      return !currentStyle;
    }
    return currentStyle === style.id;
  };

  return (
    <div className={styles.stylesWrapper}>
      <FiltersButton
        label={getDisplayText()}
        isActive={!!currentStyle}
        onClick={toggleShowStylesDropdown}
        disabled={loading}
      />
      
      <div
        className={`${styles.stylesDropdownBody} ${
          showStylesDropdown ? styles.visible : styles.hidden
        }`}
      >
        {loading ? (
          <div className={styles.loadingOption}>
            {t("search.loading") || "Loading..."}
          </div>
        ) : error ? (
          <div className={styles.errorOption}>
            {t("search.errorLoadingStyles") || "Error loading styles"}
          </div>
        ) : (
          stylesOptions.map((style) => (
            <div 
              key={style.id || "all"} 
              className={styles.styleOption}
              onClick={() => handleStyleChange(style)}
            >
              <Checkbox
                checked={isStyleSelected(style)}
                onChange={() => {}} // Empty function since we're handling click on the div
                label={style.name}
                readOnly
                greyText={true}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StylesFilter;